import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Stack,
  ListItemText,
  Checkbox,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import { api } from '../services/api';
import NeighborScanBatchField, { DEFAULT_NEIGHBOR_SCAN_BATCH } from './NeighborScanBatchField';
import { countNeighborsScanFailures } from '../utils/neighborsScanUtils';

const NeighborsScanTab = ({ devices }) => {
  const [selectedDevicesForScan, setSelectedDevicesForScan] = useState(['all']);
  const [scanInterval, setScanInterval] = useState(15);
  const [allFreq, setAllFreq] = useState(true);
  const [scanBatchSize, setScanBatchSize] = useState(DEFAULT_NEIGHBOR_SCAN_BATCH);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [scanConfirmDialogOpen, setScanConfirmDialogOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState('all'); // 'all', 'all_caps', 'all_nodes', 'specific'
  const [deviceFilterQuery, setDeviceFilterQuery] = useState('');

  // Normalize devices to always be an array - memoize to prevent recreation on every render
  const devicesArray = useMemo(() => devices || [], [devices]);

  // Filter devices to only show jnet2 compatible ones
  const jnet2Devices = useMemo(() => {
    return devicesArray.filter(device => device.compatible === 'jnet2');
  }, [devicesArray]);

  // Get eligible devices (non-CAP, connected)
  const eligibleDevices = useMemo(() => {
    return jnet2Devices.filter(device => {
      const model = device.capabilities?.model?.toLowerCase() || '';
      return !model.includes('cap') && device.connected;
    });
  }, [jnet2Devices]);

  // Get CAP devices
  const capDevices = useMemo(() => {
    return jnet2Devices.filter(device => {
      const model = device.capabilities?.model?.toLowerCase() || '';
      return model.includes('cap') && device.connected;
    });
  }, [jnet2Devices]);

  // Get Node devices
  const nodeDevices = useMemo(() => {
    return jnet2Devices.filter(device => {
      const model = device.capabilities?.model?.toLowerCase() || '';
      return !model.includes('cap') && device.connected;
    });
  }, [jnet2Devices]);

  const filteredEligibleDevices = useMemo(() => {
    const query = deviceFilterQuery.trim().toLowerCase();
    if (!query) return eligibleDevices;
    return eligibleDevices.filter((device) => {
      const name = (device.name || '').toLowerCase();
      const serial = (device.serial_number || '').toLowerCase();
      return name.includes(query) || serial.includes(query);
    });
  }, [eligibleDevices, deviceFilterQuery]);

  const renderDeviceSelectFilter = () => (
    <ListSubheader
      sx={{
        lineHeight: 1,
        py: 1,
        px: 1.5,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder="Search by name or serial..."
        fullWidth
        autoFocus
        value={deviceFilterQuery}
        onChange={(e) => setDeviceFilterQuery(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </ListSubheader>
  );

  // Handle selection mode change
  const handleSelectionModeChange = (event) => {
    const mode = event.target.value;
    setSelectionMode(mode);
    
    if (mode === 'all') {
      setSelectedDevicesForScan(['all']);
    } else if (mode === 'all_caps') {
      setSelectedDevicesForScan(capDevices.map(d => d.serial_number));
    } else if (mode === 'all_nodes') {
      setSelectedDevicesForScan(nodeDevices.map(d => d.serial_number));
    } else {
      setSelectedDevicesForScan([]);
    }
    setDeviceFilterQuery('');
  };

  // Handle device selection (for specific mode)
  const handleScanDeviceSelection = (event) => {
    const newValue = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    const previousValue = selectedDevicesForScan;
    
    // Find what was added by comparing new value with previous value
    const added = newValue.filter(item => !previousValue.includes(item));
    
    // If "all" was added
    if (added.includes('all')) {
      setSelectedDevicesForScan(['all']);
      setSelectionMode('all');
    }
    // If any individual device was added while "all" was previously selected
    else if (previousValue.includes('all') && added.length > 0) {
      // Clear "all" and select only the newly added device(s)
      setSelectedDevicesForScan(added);
      setSelectionMode('specific');
    }
    // Normal case - just update with filtered value (removing "all" if other devices are selected)
    else {
      setSelectedDevicesForScan(newValue.filter(v => v !== 'all'));
      if (newValue.length === 0) {
        setSelectionMode('all');
        setSelectedDevicesForScan(['all']);
      } else {
        setSelectionMode('specific');
      }
    }
  };

  // Handle showing scan confirmation dialog
  const handleShowScanDialog = () => {
    setScanConfirmDialogOpen(true);
  };

  // Handle neighbors scan execution
  const handleRunNeighborsScan = async () => {
    setScanConfirmDialogOpen(false);
    setScanLoading(true);
    setScanResults(null);
    
    try {
      let devicesToScan = [];
      
      if (selectionMode === 'all' || selectedDevicesForScan.includes('all')) {
        devicesToScan = eligibleDevices;
      } else if (selectionMode === 'all_caps') {
        devicesToScan = capDevices;
      } else if (selectionMode === 'all_nodes') {
        devicesToScan = nodeDevices;
      } else {
        devicesToScan = eligibleDevices.filter(d => selectedDevicesForScan.includes(d.serial_number));
      }

      console.log('Starting neighbors scan for devices:', devicesToScan.map(d => d.serial_number), 'with interval:', scanInterval, 'all_freq:', allFreq);
      const serials = devicesToScan.map((d) => d.serial_number).filter(Boolean);
      if (serials.length === 0) {
        setSnackbarMessage('No devices selected for neighbors scan');
        setSnackbarOpen(true);
        return;
      }

      const combinedResult = await api.startNeighborsScanBatch(serials, scanInterval, allFreq, scanBatchSize);
      setScanResults(combinedResult);

      const failedCount = countNeighborsScanFailures(combinedResult.results);
      if (failedCount > 0) {
        setSnackbarMessage(`Neighbors scan: ${combinedResult.commands_sent} successful, ${failedCount} failed`);
      } else {
        setSnackbarMessage(`Neighbors scan initiated for ${devicesToScan.length} devices`);
      }
      
      setSnackbarOpen(true);
      
      // Clear selections after scan attempt (successful or partial)
      // setSelectedDevicesForScan(['all']);
      // setSelectionMode('all');
    } catch (error) {
      console.error('Error running neighbors scan:', error);
      setSnackbarMessage(`Failed to start neighbors scan: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setScanLoading(false);
    }
  };

  if (jnet2Devices.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Alert severity="info">No jnet2 devices available. Please wait for jnet2 devices to connect.</Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Neighbors Scan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Trigger neighbors scanning on selected devices to discover and analyze nearby network devices.
      </Typography>

      <Grid container spacing={3}>
        {/* Selection Mode */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Selection Mode</InputLabel>
            <Select
              value={selectionMode}
              onChange={handleSelectionModeChange}
              label="Selection Mode"
            >
              <MenuItem value="all">All Eligible Devices ({eligibleDevices.length})</MenuItem>
              <MenuItem value="all_caps">All CAPs ({capDevices.length})</MenuItem>
              <MenuItem value="all_nodes">All Nodes ({nodeDevices.length})</MenuItem>
              <MenuItem value="specific">Specific Devices (Select Below)</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Specific Device Selection (only shown in specific mode) */}
        {selectionMode === 'specific' && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Devices</InputLabel>
              <Select
                multiple
                value={selectedDevicesForScan}
                onChange={handleScanDeviceSelection}
                label="Select Devices"
                renderValue={(selected) => {
                  if (selected.includes('all')) {
                    return 'All Devices';
                  }
                  return selected.length === 1 ? '1 device selected' : `${selected.length} devices selected`;
                }}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {renderDeviceSelectFilter()}
                {filteredEligibleDevices.length === 0 ? (
                  <MenuItem disabled dense>
                    <ListItemText primary="No devices match your search" />
                  </MenuItem>
                ) : (
                  filteredEligibleDevices.map((device) => (
                  <MenuItem key={device.serial_number} value={device.serial_number}>
                    <Checkbox 
                      checked={selectedDevicesForScan.includes(device.serial_number)} 
                      size="small"
                    />
                    <ListItemText 
                      primary={device.name || device.serial_number}
                      secondary={device.connected ? 'Connected' : 'Disconnected'}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: device.connected ? '#4caf50' : '#f44336',
                        ml: 1
                      }}
                    />
                  </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Scan Interval */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Scan Interval (seconds)"
            value={scanInterval}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setScanInterval(value);
              } else {
                const numValue = parseInt(value);
                if (!isNaN(numValue)) {
                  setScanInterval(numValue);
                }
              }
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value === '' || isNaN(parseInt(value))) {
                setScanInterval(15);
              }
            }}
            inputProps={{ min: 1, max: 60 }}
            helperText="How long to scan each channel"
          />
        </Grid>

        {/* All Frequencies */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>All Frequencies</InputLabel>
            <Select
              value={allFreq}
              onChange={(e) => setAllFreq(e.target.value)}
              label="All Frequencies"
            >
              <MenuItem value={true}>Yes - Scan all bands</MenuItem>
              <MenuItem value={false}>No - Known frequencies only</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Devices scanning at once */}
        <Grid item xs={12} md={6}>
          <NeighborScanBatchField value={scanBatchSize} onChange={setScanBatchSize} size="medium" />
        </Grid>

        {/* Run Scan Button */}
        <Grid item xs={12} md={6}>
          <Button
            variant="contained"
            onClick={handleShowScanDialog}
            disabled={scanLoading || (selectionMode === 'specific' && selectedDevicesForScan.length === 0)}
            startIcon={scanLoading ? <CircularProgress size={16} color="inherit" /> : null}
            fullWidth
            size="large"
            sx={{ height: '56px' }}
          >
            {scanLoading ? 'Running Scan...' : 'Run Neighbors Scan'}
          </Button>
        </Grid>

        {/* Scan Results Summary */}
        {scanResults && (
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Scan Results:</strong>
              </Typography>
              <Typography variant="body2">
                Commands sent: {scanResults.commands_sent}/{scanResults.devices_targeted}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Selected Devices Display (for specific mode) */}
      {selectionMode === 'specific' && selectedDevicesForScan.length > 0 && !selectedDevicesForScan.includes('all') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Devices ({selectedDevicesForScan.length}):
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {selectedDevicesForScan.map((deviceSerial) => {
              const device = eligibleDevices.find(d => d.serial_number === deviceSerial);
              return (
                <Chip
                  key={deviceSerial}
                  label={device?.name || deviceSerial}
                  size="small"
                  color={device?.connected ? 'success' : 'default'}
                  variant="outlined"
                  onDelete={() => {
                    setSelectedDevicesForScan(prev => prev.filter(s => s !== deviceSerial));
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Detailed Scan Results */}
      {scanResults && scanResults.results && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Detailed Results:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {scanResults.results.map((result, index) => (
              <Chip
                key={index}
                label={`${result.serial}: ${result.status}`}
                color={result.status === 'sent' ? 'success' : 'error'}
                size="small"
                title={result.status === 'sent' ? `UUID: ${result.uuid}` : `Error: ${result.error}`}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Scan Confirmation Dialog */}
      <Dialog
        open={scanConfirmDialogOpen}
        onClose={() => setScanConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Neighbors Scan
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {selectionMode === 'all' || selectedDevicesForScan.includes('all')
              ? `You are about to initiate a neighbors scan for all ${eligibleDevices.length} eligible devices with a ${scanInterval} second scan interval${allFreq ? ' scanning all frequencies' : ' scanning current frequencies only'}.`
              : selectionMode === 'all_caps'
              ? `You are about to initiate a neighbors scan for all ${capDevices.length} CAP devices with a ${scanInterval} second scan interval${allFreq ? ' scanning all frequencies' : ' scanning current frequencies only'}.`
              : selectionMode === 'all_nodes'
              ? `You are about to initiate a neighbors scan for all ${nodeDevices.length} Node devices with a ${scanInterval} second scan interval${allFreq ? ' scanning all frequencies' : ' scanning current frequencies only'}.`
              : `You are about to initiate a neighbors scan for ${selectedDevicesForScan.length} selected device${selectedDevicesForScan.length > 1 ? 's' : ''} with a ${scanInterval} second scan interval${allFreq ? ' scanning all frequencies' : ' scanning current frequencies only'}.`
            }
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>Important:</strong> This scan runs in the background on the selected devices. The devices will perform channel scanning and respond back with neighbor discovery results when ready.
          </DialogContentText>
          <DialogContentText>
            A tracker opens when the scan starts and shows each device&apos;s progress. You can continue using the
            interface normally; results are available through the standard neighbors reports as devices finish.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setScanConfirmDialogOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRunNeighborsScan}
            variant="contained"
            color="primary"
          >
            Start Scan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for scan messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={scanResults && scanResults.commands_sent < scanResults.devices_targeted ? 'warning' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default NeighborsScanTab;

